import { BotaoIcone, CampoData, Dialogo, Botao, formatarData, MenuSuspenso, Pilula, useTema, type ItemMenuSuspenso } from '@persona/ui';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Bell, Building2, CalendarDays, ChevronDown, LogOut, Moon, Search, Sun, SunMoon, UserRound } from 'lucide-react';
import { useState } from 'react';
import { api } from '../src/api/cliente';
import { usePreferencia } from '../src/navegacao/preferencias';
import { useSessao } from '../src/sessao/sessao';
import { BarraModulos } from './BarraModulos';
import { Marca } from './Marca';

type EscolhaTema = 'claro' | 'escuro' | 'sistema';

export function aplicarTema(t: EscolhaTema) {
  const raiz = document.documentElement;
  if (t === 'sistema') delete raiz.dataset.tema;
  else raiz.dataset.tema = t;
  try { localStorage.setItem('persona.tema', t); } catch { /* sem storage: vale até recarregar */ }
}

export function useEscolhaTema() {
  const [tema, gravar] = usePreferencia<EscolhaTema>('ui.tema', 'claro');
  return [tema, (t: EscolhaTema) => { aplicarTema(t); gravar(t); }] as const;
}

function hojeLocal() {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
}

export function Topo({ aoBuscar, moduloAtivo }: { aoBuscar: () => void; moduloAtivo: string | undefined }) {
  const { sessao, empresa, filial, dataBase, trocarContexto, definirDataBase, sair, estadoLicenca } = useSessao();
  const navegar = useNavigate();
  const efetivo = useTema();
  const [escolha, escolher] = useEscolhaTema();
  const [editandoData, setEditandoData] = useState(false);
  const [novaData, setNovaData] = useState<string | null>(dataBase);
  const notificacoes = useQuery({ queryKey: ['notificacoes', 'naoLidas'], queryFn: () => api.get<unknown[]>('/notificacoes?naoLidas=true'), staleTime: 60_000 });
  const naoLidas = notificacoes.data?.length ?? 0;
  const hoje = hojeLocal();
  const filialPadrao = !filial || filial.padrao;

  const itensContexto: ItemMenuSuspenso[] = (sessao?.empresas ?? []).flatMap((e) => [
    { tipo: 'titulo' as const, id: `t${e.id}`, rotulo: `${e.codigo} · ${e.nome}` },
    ...e.filiais.map((f) => ({ tipo: 'opcao' as const, id: `f${f.id}`, rotulo: `${f.codigo} · ${f.nome}`, marcado: e.id === empresa?.id && f.id === filial?.id,
      aoEscolher: () => trocarContexto(e.codigo, f.codigo) })),
  ]);

  const iniciais = (sessao?.usuario.nome ?? '?').split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase();

  return (
    <header className="topo">
      <div className="topo-esquerda">
        <button type="button" className="topo-marca" onClick={() => void navegar({ to: '/' })} aria-label="Início — Persona ERP">
          <Marca />
        </button>
        <BarraModulos moduloAtivo={moduloAtivo} />
      </div>
      <div className="topo-direita">
        <button type="button" className="topo-busca" onClick={aoBuscar} aria-keyshortcuts="Control+K" data-testid="abrir-busca">
          <Search size={15} strokeWidth={1.75} aria-hidden="true" />
          <span>Rotina, cliente ou pedido</span>
          <kbd className="atalho">Ctrl K</kbd>
        </button>
        {estadoLicenca !== 'liberado' && (
          <button type="button" className="topo-chip" data-aviso onClick={() => void navegar({ to: '/conta' })} title="Ver a situação da licença">
            {estadoLicenca === 'bloqueado' ? 'Licença bloqueada' : estadoLicenca === 'somente_leitura' ? 'Licença em modo leitura' : 'Verificar licença'}
          </button>
        )}
        <MenuSuspenso
          rotulo="Empresa e filial"
          itens={itensContexto}
          gatilho={
            <button type="button" className="topo-empresa" data-aviso={!filialPadrao || undefined} aria-label={`Empresa ${empresa?.codigo ?? ''} ${empresa?.nome ?? ''}, filial ${filial?.codigo ?? ''}. Trocar`}>
              <span className="topo-empresa-icone"><Building2 size={17} strokeWidth={1.75} aria-hidden="true" /></span>
              <span className="topo-empresa-texto">
                <span className="topo-empresa-nome">{empresa?.nome}</span>
                <span className="topo-empresa-filial">{filial ? `Filial ${filial.codigo} · ${filial.nome}` : `Empresa ${empresa?.codigo ?? ''}`}</span>
              </span>
              <ChevronDown size={15} className="texto-3" aria-hidden="true" />
            </button>
          }
        />
        <button type="button" className="topo-chip" data-aviso={dataBase !== hoje || undefined} onClick={() => { setNovaData(dataBase); setEditandoData(true); }}
          aria-label={`Data-base ${formatarData(dataBase)}${dataBase !== hoje ? ' (não é hoje)' : ''}. Alterar`}>
          <CalendarDays size={15} strokeWidth={1.75} aria-hidden="true" />
          {formatarData(dataBase)}
          {dataBase !== hoje && <span className="topo-chip-nome">não é hoje</span>}
        </button>
        <MenuSuspenso
          rotulo="Tema"
          itens={[
            { tipo: 'opcao', id: 'claro', rotulo: 'Claro', marcado: escolha === 'claro', aoEscolher: () => escolher('claro') },
            { tipo: 'opcao', id: 'escuro', rotulo: 'Escuro', marcado: escolha === 'escuro', aoEscolher: () => escolher('escuro') },
            { tipo: 'opcao', id: 'sistema', rotulo: 'Seguir o sistema', marcado: escolha === 'sistema', aoEscolher: () => escolher('sistema') },
          ]}
          gatilho={<BotaoIcone rotulo="Tema" icone={escolha === 'sistema' ? <SunMoon size={18} strokeWidth={1.75} /> : efetivo === 'escuro' ? <Moon size={18} strokeWidth={1.75} /> : <Sun size={18} strokeWidth={1.75} />} />}
        />
        <span className="topo-sino">
          <BotaoIcone rotulo={naoLidas ? `Notificações: ${naoLidas} não lidas` : 'Notificações'} icone={<Bell size={18} strokeWidth={1.75} />} disabled={!naoLidas} />
          {naoLidas > 0 && <Pilula cor="acento" className="topo-contador" aria-hidden="true">{naoLidas}</Pilula>}
        </span>
        <MenuSuspenso
          rotulo="Usuário"
          itens={[
            { tipo: 'titulo', id: 'u', rotulo: sessao?.usuario.nome ?? '' },
            { id: 'diag', rotulo: 'Diagnóstico', aoEscolher: () => void navegar({ to: '/diagnostico' }) },
            { tipo: 'separador', id: 's' },
            { id: 'conta', rotulo: 'Minha conta', icone: <UserRound size={15} strokeWidth={1.75} />, aoEscolher: () => void navegar({ to: '/conta' }) },
            { id: 'sair', rotulo: 'Sair', icone: <LogOut size={15} strokeWidth={1.75} />, perigo: true, aoEscolher: () => void sair().then(() => navegar({ to: '/entrar', search: { volta: undefined } })) },
          ]}
          gatilho={<button type="button" className="avatar" aria-label={`Usuário ${sessao?.usuario.nome ?? ''}`}>{iniciais}</button>}
        />
      </div>
      <Dialogo aberto={editandoData} aoFechar={() => setEditandoData(false)} titulo="Data-base da sessão" largura="p"
        rodape={<><Botao onClick={() => setEditandoData(false)}>Cancelar</Botao><Botao variante="primario" disabled={!novaData} onClick={() => { if (novaData) definirDataBase(novaData); setEditandoData(false); }}>Aplicar</Botao></>}>
        <p style={{ marginTop: 0 }}>Usada como padrão nas datas de movimento. Vale até sair ou trocar de novo.</p>
        <CampoData nome="dataBase" rotulo="Data-base" valor={novaData} aoMudar={setNovaData} dataBase={dataBase} hoje={hoje} ajuda="h = hoje · +7 = daqui a 7 dias" />
      </Dialogo>
    </header>
  );
}
